import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import {
  useGetAccountInfo,
  useGetProcedureBuilderProcedures,
  useUpdateProcedureVisibilty,
  useGetProcedureDetails,
  useProcedureReferences,
  useArchiveProcedure,
  useRenameProcedure,
  useForceRenameProcedure,
  useConvertToGlobalProcedure,
  useConvertToNormalProcedure,
  useCloneProcedure,
  useUndoProcedureContent,
  usePublishProcedure,
  useSaveProcedure,
  useCreateProcedure,
  useUpdateProcedureAutoSave,
  useSaveTemplate,
  useUpdateTemplate,
  useCreateBlobDirectory,
  useUploadFile,
  useDeleteBlobFile,
  useSaveProcedureAsPDF,
  useSaveProcedureAsWord,
  useSaveProcedureAsHtml,
  useCleanProcedure,
  useGetBrokenLinksForDismissal,
} from 'hooks';

import * as Icon from 'assets/icons';

import { confirmAlert } from 'utils/confirm-alert';
import { showSuccessToast, showErrorToast } from 'utils/showToast';
import { lazyRun } from 'utils/useInterval';
import {
  checkIfIframe,
  removeUrlParams,
  stripAndTrimHTML,
} from 'utils/procedureBuilderHelpers';

import {
  ProcedureReturnType,
  ProcedureBuilderListType,
} from 'types/procedureBuilder';
import { ResultModal, ErrorModal } from 'types/modalTypes';
import {
  useProcedureBuilderType,
  ConfirmCloneHandlerType,
} from './procedureTypes';

export default function useProcedureBuilder({
  accountId,
  pid,
  mode,
}: useProcedureBuilderType): any {
  const [procedures, setProcedures] = useState<Array<ProcedureBuilderListType>>(
    [],
  );
  const [searchValue, setSearchValue] = useState<string>();
  const [renameProc, setRenameProc] = useState<ProcedureReturnType>();
  const [appliedFilterOption, setAppliedFilterOption] = useState<string>('');
  const [userTyped, setUserTyped] = useState<boolean>(false);
  const [showBrokenLinks, setShowBrokenLinks] = useState<boolean>(false);
  const [content, setContent] = useState('');
  const [editorChanges, setEditorChanges] = useState<boolean>(false); // dedicated state variable for prompt
  const queryClient = useQueryClient();
  const history = useHistory();

  const tempId = localStorage.getItem('fromSearch');
  const [selectedProcedures, setSelectedProcedures] = useState<
    Array<number> | undefined
  >(() => {
    if (pid) return [pid];
    if (tempId) return [JSON.parse(tempId)];
    else return;
  });

  const selectdProcedureRef = useRef<number>();
  const procedureId = useMemo(() => {
    if (selectdProcedureRef?.current) {
      queryClient.removeQueries(
        ['get_procedure', accountId, selectdProcedureRef.current],
        { exact: true },
      );
    } else {
      queryClient.removeQueries(['get_procedure', accountId]);
    }
    const newProcedureId =
      selectedProcedures && selectedProcedures.length === 1
        ? selectedProcedures[0]
        : 0;
    selectdProcedureRef.current = newProcedureId;
    return newProcedureId;
  }, [accountId, selectedProcedures]);

  const { data: accountInfo, isLoading: isAccountInfoLoading } =
    useGetAccountInfo(accountId);
  const { data: procedureList, isLoading: isProcedureLoading } =
    useGetProcedureBuilderProcedures(accountId);

  const { mutate: saveAsPDF, isLoading: isSavePDFLoading } =
    useSaveProcedureAsPDF();

  const { mutate: saveAsHtml, isLoading: isSaveHtmlLoading } =
    useSaveProcedureAsHtml();

  const { mutate: saveAsWord, isLoading: isSaveWordLoading } =
    useSaveProcedureAsWord();

  const { mutate: saveTemplate, isLoading: isSaveTemplateLoading } =
    useSaveTemplate();

  const { mutate: updateTemplate, isLoading: isUpdateTemplateLoading } =
    useUpdateTemplate();

  const {
    mutate: updateProcedureVisibility,
    isLoading: isProcedureVisibilityLoading,
  } = useUpdateProcedureVisibilty();

  const { mutate: formatCleanProcedure, isLoading: isTemplateCleanupLoading } =
    useCleanProcedure();

  const {
    mutate: getProcedureReference,
    isLoading: isProcedureReferenceLoading,
  } = useProcedureReferences();

  const {
    mutate: undoProcedureContent,
    isLoading: isUndoProcedureContentLoading,
  } = useUndoProcedureContent();

  const { mutate: archiveProcedure, isLoading: isArchiveProcedureLoading } =
    useArchiveProcedure();

  const {
    mutate: updateProcedureAutoSaveOption,
    isLoading: isUpdateProcedureAutoSaveoption,
  } = useUpdateProcedureAutoSave();

  const { mutate: renameProcedure, isLoading: isRenameProcedureLoading } =
    useRenameProcedure();

  //force rename for making both side names same
  const {
    mutate: forceRenameProcedure,
    isLoading: isForceRenameProcedureLoading,
  } = useForceRenameProcedure();

  const {
    mutate: convertToGlobalProcedure,
    isLoading: isConvertToGlobalProcedureLoading,
  } = useConvertToGlobalProcedure();

  const {
    mutate: convertToNormalProcedure,
    isLoading: isConvertToNormalProcedureLoading,
  } = useConvertToNormalProcedure();

  const { mutate: cloneProcedure, isLoading: isCloneProcedureLoading } =
    useCloneProcedure();

  const { mutate: publishProcedure, isLoading: isPublishProcedureLoading } =
    usePublishProcedure();

  const { mutate: autoPublishProcedure } = usePublishProcedure();

  const { mutate: saveProcedure, isLoading: isSaveProcedureLoading } =
    useSaveProcedure();
  const { mutate: autoSaveProcedure } = useSaveProcedure();

  const {
    data: procedureDetails,
    isLoading: isProcedureDetailsLoading,
    refetch,
  } = useGetProcedureDetails({
    accountId,
    procedureId,
  });
  const { mutate: createProcedure, isLoading: isCreateProcedureLoading } =
    useCreateProcedure();

  const { data: brokenLinks, isLoading: isBrokenLinksLoading } =
    useGetBrokenLinksForDismissal({
      accountId,
      showPanel: true,
      payloadString: '',
    });

  const {
    mutate: createBlobDirectory,
    isLoading: isCreateBlobDirectoryLoading,
  } = useCreateBlobDirectory();
  const { mutate: uploadFile, isLoading: isuploadFileLoading } =
    useUploadFile();
  const { mutate: deleteFile, isLoading: isDeleteFileLoading } =
    useDeleteBlobFile();

  const isLoading =
    isAccountInfoLoading ||
    isProcedureLoading ||
    isProcedureVisibilityLoading ||
    isProcedureDetailsLoading ||
    isProcedureReferenceLoading ||
    isArchiveProcedureLoading ||
    isUpdateProcedureAutoSaveoption ||
    isConvertToGlobalProcedureLoading ||
    isConvertToNormalProcedureLoading ||
    isCloneProcedureLoading ||
    isPublishProcedureLoading ||
    isCreateProcedureLoading ||
    // isSaveProcedureLoading ||
    isProcedureDetailsLoading ||
    isUndoProcedureContentLoading ||
    isRenameProcedureLoading ||
    isSaveTemplateLoading ||
    isUpdateTemplateLoading ||
    isCreateBlobDirectoryLoading ||
    isuploadFileLoading ||
    isDeleteFileLoading ||
    isTemplateCleanupLoading ||
    isForceRenameProcedureLoading ||
    isBrokenLinksLoading ||
    isSavePDFLoading ||
    isSaveHtmlLoading ||
    isSaveWordLoading;

  const toggleBrokenLinks = () => {
    setShowBrokenLinks(!showBrokenLinks);
  };

  useEffect(() => {
    setRenameProc(undefined);
  }, [searchValue]);

  useEffect(() => {
    let newProcedures: Array<ProcedureBuilderListType> = [];
    if (procedureList && procedureList?.Data?.length > 0) {
      const sorted = procedureList?.Data?.sort((a: any, b: any) =>
        a.ProcedureName.split('_')
          .join('')
          .localeCompare(b.ProcedureName.split('_').join('')),
      );
      if (sorted) newProcedures = [...sorted];
      if (searchValue) {
        newProcedures = newProcedures.filter(item => {
          const isSerchedItem = item.ProcedureName.toLowerCase().includes(
            searchValue.toLowerCase(),
          );
          return isSerchedItem;
        });
      }

      if (appliedFilterOption) {
        if (appliedFilterOption === 'Procedures with broken links')
          newProcedures = newProcedures.filter(item => {
            const filteredItem = item.BrokenLinksCount > 0;
            return filteredItem;
          });
        if (appliedFilterOption === 'Linked procedures')
          newProcedures = newProcedures.filter(item => {
            const filteredItem = item.IsLinked === true;
            return filteredItem;
          });
        if (appliedFilterOption === 'Global procedures')
          newProcedures = newProcedures.filter(item => {
            const filteredItem = item.IsGlobal === true;
            return filteredItem;
          });
        if (appliedFilterOption === 'Visible procedures')
          newProcedures = newProcedures.filter(item => {
            const filteredItem = item.IsVisible === true;
            return filteredItem;
          });
        if (appliedFilterOption === 'Invisible procedures')
          newProcedures = newProcedures.filter(item => {
            const filteredItem = item.IsVisible === false;
            return filteredItem;
          });
        if (appliedFilterOption === 'Un-published procedures')
          newProcedures = newProcedures.filter(item => {
            const filteredItem =
              item.HasSavedChangesThatAreNotPublished === true;
            return filteredItem;
          });
      }
    }
    setProcedures([...newProcedures]);
  }, [procedureList, searchValue, appliedFilterOption]);

  useEffect(() => {
    localStorage.removeItem('fromSearch');
    if (
      procedureList?.Data &&
      procedureList?.Data?.length > 0 &&
      !selectedProcedures
    ) {
      queryClient.removeQueries(
        ['get_procedure', accountId, procedureList.Data[0]?.ProcedureId],
        { exact: true },
      );
      setSelectedProcedures([procedureList.Data[0]?.ProcedureId]);
    }
  }, [procedureList, selectedProcedures, accountId]);

  const accountNumber = useMemo(
    () =>
      `${accountInfo?.accountInfoReturn?.Data.CID}${'-'}${
        accountInfo?.accountInfoReturn?.Data.SID
      }${'-'}${accountInfo?.accountInfoReturn?.Data.PID}`,
    [accountInfo],
  );

  const totalBrokenLinksCount = useMemo(() => {
    if (brokenLinks && brokenLinks.Data && brokenLinks.Data.length > 0) {
      let totalCounts = 0;
      brokenLinks.Data.forEach((item: any) =>
        item.ProcedureLinks.forEach((link: any) => {
          if (link?.Link?.IgnoreIsBroken === false) {
            totalCounts = totalCounts + link.Count;
          }
        }),
      );
      return totalCounts;
    }
  }, [brokenLinks]);

  const isUnpublishedProceduresPresent = useMemo(() => {
    if (procedureList) {
      const proceduresList = [...procedureList.Data];
      return (
        proceduresList &&
        proceduresList.filter(item => {
          if (item.HasSavedChangesThatAreNotPublished === true) {
            return true;
          } else {
            return false;
          }
        })
      );
    }
  }, [procedureList]);

  /*********SAVE PROCEDURE******************************************************************/

  //Do not show again checkbox//
  const handleSaveModalVisiblity = (e: any) => {
    if (e.target.checked === true) {
      localStorage.setItem('saveModalConfirmation', JSON.stringify(true));
    } else {
      localStorage.removeItem('saveModalConfirmation');
    }
  };
  //Function to handle apis
  const confirmSaveProcedure = ({
    accountId,
    params,
  }: ConfirmCloneHandlerType) => {
    saveProcedure(
      {
        AccountId: accountId,
        params: { ...params },
      },
      {
        onSuccess: () => {
          setUserTyped(false);
          setEditorChanges(false);
          showSuccessToast({
            message: 'Your changes were saved successfully.',
          });
          // invalidate procedure list query to reflect any changes
          queryClient.invalidateQueries(
            ['get_procedure_builder_procedures', accountId],
            { refetchInactive: true },
          );
          queryClient.invalidateQueries(
            ['get_procedure', accountId, params.Id],
            { refetchInactive: true },
          );
        },
        onError: (error: ErrorModal) => {
          showErrorToast({
            message: error.Errors[0].Message,
          });
        },
      },
    );
  };
  //Main function handling save
  function saveContent(cb?: () => void, conversion?: boolean) {
    let content = '';
    const tempValue = localStorage.getItem('saveModalConfirmation');
    const showConfirmation = tempValue === null ? false : JSON.parse(tempValue);
    lazyRun(() => {
      // cancels all lazy runs scheduled prior
    }, 0);

    //no need of auto save for policy menu
    if (
      procedureDetails?.Data?.AccountProcedure?.Procedure?.Name ===
      '0000_POLICIESMENU.html'
    )
      return;

    if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
      content = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
        procedureDetails?.Data?.AccountProcedure?.AccountId
      }/Policies/'></head><body>${(
        window as any
      )?.tinymce?.activeEditor?.getContent()}</body></html>`;
    }

    // if (
    //   procedureDetails?.Data?.AccountProcedure?.Procedure?.Name ===
    //     '0000_POLICIESMENU.html' &&
    //   stripAndTrimHTML(procedureDetails?.Data?.Content) !== '' &&
    //   stripAndTrimHTML((window as any)?.tinymce?.activeEditor?.getContent()) ===
    //     ''
    // ) {
    //   confirmAlert({
    //     message:
    //       'The Policy menu cannot be left empty. Your last saved entries will be restored.',
    //     closeOnClickOutside: false,
    //     buttons: [
    //       {
    //         label: 'Ok',
    //         onClick: async () => {
    //           const result = await refetch();
    //           let content = result?.data?.Data?.Content ?? '';
    //           (window as any)?.tinymce?.activeEditor?.setContent(content);
    //           return;
    //         },
    //         className: 'pr-btn-primary',
    //       },
    //     ],
    //   });
    //   return;
    // }
    // let updatedContent = content;
    // const isIframePresent = checkIfIframe(content);
    // if (isIframePresent === true) {
    //   updatedContent = removeUrlParams(content);
    // } else

    const saveParams = {
      Id: procedureDetails?.Data?.AccountProcedure.ProcedureId || 0,
      Content: content || '',
    };
    const pList = procedureList?.Data?.filter((item: any) =>
      selectedProcedures?.find(id => item.ProcedureId === id),
    );
    const procFileIds = pList && pList.map((item: any) => item.FileId);
    if (cb) {
      if (conversion) {
        saveProcedure(
          {
            AccountId: accountId,
            params: { ...saveParams },
          },
          {
            onSuccess: (mutationResult: ResultModal) => {
              if (mutationResult.Data) {
                if (procFileIds)
                  publishProcedure(
                    {
                      accountId: accountId,
                      params: [...procFileIds],
                    },
                    {
                      onSuccess: cb,
                    },
                  );
              } else {
                showErrorToast({
                  message: 'Something went wrong.',
                });
              }
            },
            onError: (error: ErrorModal) => {
              showErrorToast({
                message: error.Errors[0].Message,
              });
            },
          },
        );
      } else {
        saveProcedure(
          {
            AccountId: accountId,
            params: { ...saveParams },
          },
          {
            onSettled: cb,
          },
        );
      }
    } else {
      if (!showConfirmation) {
        confirmAlert({
          dontShowAgain: true,
          handleModalVisibility: handleSaveModalVisiblity,
          message:
            'This will save your progress to the current version, but it will not be available to Proteus Call Center until published.',
          buttons: [
            {
              label: 'Ok',
              onClick: () => {
                confirmSaveProcedure({
                  accountId: accountId,
                  params: { ...saveParams },
                });
              },
              className: 'pr-btn-primary',
            },
            {
              label: 'Cancel',
              onClick: () => {
                //
              },
            },
          ],
        });
      } else {
        confirmSaveProcedure({
          accountId: accountId,
          params: { ...saveParams },
        });
      }
    }
  }
  /*********SAVE PROCEDURE******************************************************************/

  /********* AUTO SAVE AND PUBLISH******************************************************************/
  const autoPublishPolicyContent = (cb?: () => void) => {
    lazyRun(() => {
      // cancels all lazy runs scheduled prior
    }, 0);
    if (
      procedureDetails?.Data?.AccountProcedure?.Procedure?.Name ===
      '0000_POLICIESMENU.html'
    )
      return;
    let content = '';
    if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
      content = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
        procedureDetails?.Data?.AccountProcedure?.AccountId
      }/Policies/'></head><body>${(
        window as any
      )?.tinymce?.activeEditor?.getContent()}</body></html>`;
    }
    // if (
    //   procedureDetails?.Data?.AccountProcedure?.Procedure?.Name ===
    //   '0000_POLICIESMENU.html' &&
    //   stripAndTrimHTML(procedureDetails?.Data?.Content) !== '' &&
    //   stripAndTrimHTML((window as any)?.tinymce?.activeEditor?.getContent()) ===
    //   ''
    // ) {
    //   confirmAlert({
    //     message:
    //       'The Policy menu cannot be left empty. Your last saved entries will be restored.',
    //     closeOnClickOutside: false,
    //     buttons: [
    //       {
    //         label: 'Ok',
    //         onClick: async () => {
    //           const result = await refetch();
    //           let content = result?.data?.Data?.Content ?? '';
    //           (window as any)?.tinymce?.activeEditor?.setContent(content);
    //           return;
    //         },
    //         className: 'pr-btn-primary',
    //       },
    //     ],
    //   });
    //   return;
    // }

    // let updatedContent;
    // const isIframePresent = checkIfIframe(content);
    // if (isIframePresent === true) {
    //   updatedContent = removeUrlParams(content);
    // } else updatedContent = content;

    const saveParams = {
      Id: procedureDetails?.Data.AccountProcedure.ProcedureId || 0,
      Content: content || '',
      ProcedureCategoryId:
        procedureDetails?.Data.AccountProcedure.Procedure.ProcedureCategoryId ||
        null,
    };
    const pList = procedureList?.Data?.filter((item: any) =>
      selectedProcedures?.find(id => item.ProcedureId === id),
    );
    const procFileIds = pList && pList.map((item: any) => item.FileId);
    autoSaveProcedure(
      {
        AccountId: accountId,
        params: { ...saveParams },
      },
      {
        onSuccess: (mutationResult: any) => {
          setUserTyped(false);
          setEditorChanges(false);
          if (mutationResult) {
            if (procFileIds)
              autoPublishProcedure(
                {
                  accountId: accountId,
                  params: [...procFileIds],
                },
                {
                  onSuccess: cb,
                },
              );
          }
        },
      },
    );
  };

  const autoSaveContent = useCallback(
    (cb?: () => void, contentChange?: boolean) => {
      if (
        procedureDetails?.Data?.AccountProcedure?.Procedure?.Name ===
        '0000_POLICIESMENU.html'
      )
        return;

      if (!(window as any)?.tinymce?.activeEditor) {
        console.log(
          'autosavecontent is cancelled due to absence of an active editor',
        );
        return;
      }
      const currentPage =
        history.location.pathname.split('/')[
          history.location.pathname.split('/').length - 1
        ];

      if (currentPage === 'accountInfo') return;
      else {
        let content = '';
        if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
          content = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
            procedureDetails?.Data?.AccountProcedure?.AccountId
          }/Policies/'></head><body>${(
            window as any
          )?.tinymce?.activeEditor?.getContent()}</body></html>`;
        }
        // let updatedContent;
        // const isIframePresent = checkIfIframe(content);
        // if (isIframePresent === true) {
        //   updatedContent = removeUrlParams(content);
        // } else updatedContent = content;

        if (
          (window as any)?.tinymce?.activeEditor?.isDirty() ||
          content !== procedureDetails?.Data?.Content ||
          contentChange
        ) {
          const saveParams = {
            Id: procedureDetails?.Data?.AccountProcedure.ProcedureId || 0,
            Content: content || '',
          };
          autoSaveProcedure(
            {
              AccountId: accountId,
              params: saveParams,
            },
            {
              onSuccess: cb,
            },
          );
        }
      }
    },
    [procedureDetails, autoSaveProcedure, accountId],
  );

  /********* AUTO SAVE AND PUBLISH******************************************************************/

  /*********CLONE PROCEDURE******************************************************************/
  //Do not show again checkbox//
  const handleCloneModalVisiblity = (e: any) => {
    if (e.target.checked === true) {
      localStorage.setItem('cloneModalConfirmation', JSON.stringify(true));
    } else {
      localStorage.removeItem('cloneModalConfirmation');
    }
  };
  //clone function api call
  const confirmCloneProcedure = ({
    procedureFileIds,
    accountId,
  }: {
    procedureFileIds: Array<string>;
    accountId: number;
  }) => {
    cloneProcedure(
      {
        procedureFileIds: [...procedureFileIds],
        accountId: accountId,
      },
      {
        onSuccess: (mutationResult: ResultModal) => {
          setSearchValue('');
          if (
            mutationResult &&
            mutationResult.Data.length &&
            mutationResult.Data.length === 1
          ) {
            if (mutationResult.Data[0])
              setSelectedProcedures([mutationResult.Data[0]]);
            queryClient.invalidateQueries(
              ['get_procedure', mutationResult.Data[0]],
              {
                refetchInactive: true,
              },
            );
            if (procedureDetails?.Data) {
              setRenameProc({
                AccountId: procedureDetails.Data.AccountProcedure.AccountId,
                ProcedureId: mutationResult.Data[0],
                ProcedureName:
                  procedureDetails.Data.AccountProcedure.Procedure.Name,
              });
            }
          }
          showSuccessToast({
            message: 'Your changes were saved successfully.',
          });
        },
        onError: (error: ErrorModal) => {
          showErrorToast({
            message: error.Errors[0].Message,
          });
        },
      },
    );
  };

  //Main function to handle clone
  const handleCloneProcedure = () => {
    const tempValue = localStorage.getItem('cloneModalConfirmation');
    const showConfirmation = tempValue === null ? false : JSON.parse(tempValue);
    if (selectedProcedures && selectedProcedures.length >= 1) {
      const pList = procedureList?.Data?.filter((item: any) =>
        selectedProcedures.find(id => item.ProcedureId === id),
      );
      const procFileIds = pList && pList.map((item: any) => item.FileId);
      if (procFileIds)
        if (!showConfirmation) {
          confirmAlert({
            dontShowAgain: true,
            handleModalVisibility: handleCloneModalVisiblity,
            message: 'This will create a new procedure with the same contents.',
            buttons: [
              {
                label: 'Ok',
                onClick: () => {
                  confirmCloneProcedure({
                    procedureFileIds: [...procFileIds],
                    accountId: accountId,
                  });
                },
                className: 'pr-btn-primary',
              },
              {
                label: 'Cancel',
                onClick: () => {
                  //
                },
              },
            ],
          });
        } else {
          confirmCloneProcedure({
            procedureFileIds: [...procFileIds],
            accountId: accountId,
          });
        }
    }
  };
  /*********CLONE PROCEDURE******************************************************************/

  /*********PUBLISH******************************************************************/
  // Function to handle publish api (Single,Multiple)
  function publishProcedureApi(
    procFileIds: Array<string>,
    makeProceduresVisible?: boolean,
  ) {
    publishProcedure(
      {
        accountId: accountId,
        params: [...procFileIds],
        makeProceduresVisible: makeProceduresVisible,
      },
      {
        onSuccess: (mutationResult: ResultModal) => {
          if (mutationResult.Data) {
            showSuccessToast({
              message: 'Procedure(s) Published Successfully.',
            });
          }
        },
        onError: (error: ErrorModal) => {
          showErrorToast({
            message: error.Errors[0].Message,
          });
        },
      },
    );
  }
  //*********SINGLE PUBLISH******************************************************************//
  //Do not show again checkbox//
  const handleSinglePublishModalVisiblity = (e: any) => {
    if (e.target.checked === true) {
      localStorage.setItem('publishConfirmation', JSON.stringify(true));
    } else {
      localStorage.removeItem('publishConfirmation');
    }
  };

  //Function to save,check visibility,broken link presence and publish
  const confirmationSinglePublish = ({
    saveParams,
    brokenLinkProcedure,
    procFileIds,
    makeProceduresVisible,
  }: {
    saveParams: {
      Id: number;
      Content: string;
      ProcedureCategoryId: any;
    };
    brokenLinkProcedure: ProcedureBuilderListType | undefined;
    procFileIds: Array<string>;
    makeProceduresVisible?: boolean;
  }) => {
    // let updatedContent;
    // const isIframePresent = checkIfIframe(saveParams.Content);
    // if (isIframePresent === true) {
    //   updatedContent = saveParams.Content
    // } else 
    //   updatedContent = saveParams.Content;

    saveProcedure(
      {
        AccountId: accountId,
        params: {
          Id: saveParams.Id,
          Content: saveParams.Content,
        },
      },
      {
        onSuccess: (mutationResult: ResultModal) => {
          setUserTyped(false);
          setEditorChanges(false);
          if (mutationResult.Data) {
            if (brokenLinkProcedure) {
              confirmAlert({
                message:
                  'You have broken links in the procedure(s). Do you want to continue?',
                buttons: [
                  {
                    label: 'Yes',
                    onClick: () => {
                      if (makeProceduresVisible)
                        publishProcedureApi(procFileIds, makeProceduresVisible);
                      else publishProcedureApi(procFileIds);
                    },
                    className: 'pr-btn-primary',
                  },
                  {
                    label: 'No',
                    onClick: () => {
                      //
                    },
                  },
                ],
              });
            } else {
              if (makeProceduresVisible)
                publishProcedureApi(procFileIds, makeProceduresVisible);
              else publishProcedureApi(procFileIds);
            }
          } else {
            showErrorToast({
              message: 'Something went wrong.',
            });
          }
        },
        onError: (error: ErrorModal) => {
          showErrorToast({
            message: error.Errors[0].Message,
          });
        },
      },
    );
  };
  // Main function to handle single publish
  function handlePublishSingleProcedure() {
    const tempValue = localStorage.getItem('publishConfirmation');
    const showConfirmation = tempValue === null ? false : JSON.parse(tempValue);
    lazyRun(() => {
      // cancels all scheduled auto saves
    }, 0);
    let info = '';
    if (mode === 'Edit') {
      if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
        info = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
          procedureDetails?.Data?.AccountProcedure?.AccountId
        }/Policies/'></head><body>${(
          window as any
        )?.tinymce?.activeEditor?.getContent()}</body></html>`;
      }
      // if (
      //   procedureDetails?.Data?.AccountProcedure?.Procedure?.Name ===
      //   '0000_POLICIESMENU.html' &&
      //   stripAndTrimHTML(procedureDetails?.Data?.Content) !== '' &&
      //   stripAndTrimHTML(
      //     (window as any)?.tinymce?.activeEditor?.getContent(),
      //   ) === ''
      // ) {
      //   confirmAlert({
      //     message:
      //       'The Policy menu cannot be left empty. Your last saved entries will be restored.',
      //     closeOnClickOutside: false,
      //     buttons: [
      //       {
      //         label: 'Ok',
      //         onClick: async () => {
      //           const result = await refetch();
      //           let content = result?.data?.Data?.Content ?? '';
      //           (window as any)?.tinymce?.activeEditor?.setContent(content);
      //           return;
      //         },
      //         className: 'pr-btn-primary',
      //       },
      //     ],
      //   });
      //   return;
      // }
      setContent(info);
    }
    if (selectedProcedures && selectedProcedures.length >= 1) {
      const pList = procedureList?.Data?.filter((item: any) =>
        selectedProcedures.find(id => item.ProcedureId === id),
      );
      const procFileIds = pList && pList.map((item: any) => item.FileId);
      const brokenLinkProcedure =
        pList && pList.find((item: any) => item.BrokenLinksCount > 0);
      const saveParams = {
        Id: procedureDetails?.Data?.AccountProcedure?.ProcedureId || 0,
        Content: mode === 'Edit' ? info || '' : content || '',
        ProcedureCategoryId:
          procedureDetails?.Data?.AccountProcedure?.Procedure
            ?.ProcedureCategoryId || null,
      };
      if (procFileIds)
        if (
          procedureDetails &&
          procedureDetails.Data.AccountProcedure.Procedure?.RecentVersion !==
            null &&
          procedureDetails.Data.AccountProcedure.Procedure.CurrentVersion > 0
        ) {
          if (!showConfirmation) {
            confirmAlert({
              message:
                'This will finalize the current version and make it available to the Proteus Call Center. Previous versions will remain accessible in the Revision History',
              dontShowAgain: true,
              handleModalVisibility: handleSinglePublishModalVisiblity,
              buttons: [
                {
                  label: 'Ok',
                  onClick: () => {
                    confirmationSinglePublish({
                      saveParams,
                      brokenLinkProcedure,
                      procFileIds,
                    });
                  },
                  className: 'pr-btn-primary',
                },
                {
                  label: 'Cancel',
                  onClick: () => {
                    //
                  },
                },
              ],
            });
          } else {
            confirmationSinglePublish({
              saveParams,
              brokenLinkProcedure,
              procFileIds,
            });
          }
        } else {
          confirmAlert({
            buttonOrder: true,
            message:
              'You are about to publish one or more procedures for the first time. Would you also like to add those new procedures to the Policies Menu?',
            buttons: [
              {
                label: 'Cancel',
                onClick: () => {
                  //
                },
                className: 'pr-btn-secondary',
              },
              {
                label: 'No',
                onClick: () => {
                  confirmationSinglePublish({
                    saveParams,
                    brokenLinkProcedure,
                    procFileIds,
                    makeProceduresVisible: false,
                  });
                },
              },
              {
                label: 'Yes',
                onClick: () => {
                  confirmationSinglePublish({
                    saveParams,
                    brokenLinkProcedure,
                    procFileIds,
                    makeProceduresVisible: true,
                  });
                },
                className: 'pr-btn-primary',
              },
            ],
          });
        }
    }
  }
  //*********SINGLE PUBLISH******************************************************************//

  //*********MULTIPLE PUBLISH******************************************************************//
  //Do not show again checkbox//
  const handleMultiplePublishModalVisiblity = (e: any) => {
    if (e.target.checked === true) {
      localStorage.setItem('publishConfirmation', JSON.stringify(true));
    } else {
      localStorage.removeItem('publishConfirmation');
    }
  };
  const confirmMultiplePublish = ({
    brokenLinkProcedure,
    procFileIds,
    makeProceduresVisible,
  }: {
    brokenLinkProcedure: Array<ProcedureBuilderListType> | undefined;
    procFileIds: Array<string>;
    makeProceduresVisible?: boolean;
  }) => {
    setTimeout(() => {
      if (brokenLinkProcedure && brokenLinkProcedure.length >= 1) {
        confirmAlert({
          message:
            'You have broken links in the procedure(s). Do you want to continue?',
          buttons: [
            {
              label: 'Yes',
              onClick: () => {
                if (makeProceduresVisible)
                  publishProcedureApi(procFileIds, makeProceduresVisible);
                else publishProcedureApi(procFileIds);
              },
              className: 'pr-btn-primary',
            },
            {
              label: 'No',
              onClick: () => {
                //
              },
            },
          ],
        });
      } else {
        if (makeProceduresVisible)
          publishProcedureApi(procFileIds, makeProceduresVisible);
        else publishProcedureApi(procFileIds);
      }
    }, 10);
  };

  function handlePublishMultipleProcedures() {
    const tempValue = localStorage.getItem('publishConfirmation');
    const showConfirmation = tempValue === null ? false : JSON.parse(tempValue);
    if (selectedProcedures && selectedProcedures.length >= 1) {
      const pList = procedureList?.Data?.filter((item: any) =>
        selectedProcedures.find(id => item.ProcedureId === id),
      );
      const procFileIds = pList && pList.map((item: any) => item.FileId);
      const draftProceduresPresent =
        pList &&
        pList.filter((item: any) => {
          if (item?.IsDraft === true) return item;
        });

      const brokenLinkProcedure =
        pList && pList.filter((item: any) => item.BrokenLinksCount > 0);
      if (procFileIds) {
        if (!draftProceduresPresent) {
          if (!showConfirmation) {
            confirmAlert({
              dontShowAgain: true,
              handleModalVisibility: handleMultiplePublishModalVisiblity,
              message:
                'This will finalize the current version and make it available to the Proteus Call Center. Previous versions will remain accessible in the Revision History',
              buttons: [
                {
                  label: 'Ok',
                  onClick: () => {
                    confirmMultiplePublish({
                      brokenLinkProcedure,
                      procFileIds,
                    });
                  },

                  className: 'pr-btn-primary',
                },
                {
                  label: 'Cancel',
                  onClick: () => {
                    //
                  },
                },
              ],
            });
          } else {
            confirmMultiplePublish({
              brokenLinkProcedure,
              procFileIds,
            });
          }
        } else {
          confirmAlert({
            buttonOrder: true,
            message:
              'You are about to publish one or more procedures for the first time. Would you also like to add those new procedures to the Policies Menu?',
            buttons: [
              {
                label: 'Cancel',
                onClick: () => {
                  //
                },
                className: 'pr-btn-secondary',
              },
              {
                label: 'No',
                onClick: () => {
                  confirmMultiplePublish({
                    brokenLinkProcedure,
                    procFileIds,
                    makeProceduresVisible: false,
                  });
                },
              },
              {
                label: 'Yes',
                onClick: () => {
                  confirmMultiplePublish({
                    brokenLinkProcedure,
                    procFileIds,
                    makeProceduresVisible: true,
                  });
                },
                className: 'pr-btn-primary',
              },
            ],
          });
        }
      }
    }
  }
  //*********MULTIPLE PUBLISH******************************************************************//

  /*********PUBLISH******************************************************************/

  /*********PROCEDURE CONVERSION************************************************************/

  const isConvertableToGlobal = () => {
    if (selectedProcedures && selectedProcedures.length >= 1) {
      const pList = procedureList?.Data?.filter((item: any) =>
        selectedProcedures.find(id => item.ProcedureId === id),
      );
      const isConvertable =
        pList &&
        pList.find(
          (item: any) => item.IsLinked === true || item.IsGlobal === true,
        );
      return isConvertable;
    } else return true;
  };
  //To check if selected procedures has any global procedure
  const hasGlobalProcedures = useMemo(() => {
    if (!selectedProcedures?.length) return false;

    return (
      procedureList?.Data?.some(
        (item: any) =>
          selectedProcedures.includes(item.ProcedureId) && item.IsGlobal,
      ) || false
    );
  }, [selectedProcedures, procedureList]);

  const isConvertableToNormal = () => {
    if (selectedProcedures && selectedProcedures.length >= 1) {
      const pList = procedureList?.Data?.filter((item: any) =>
        selectedProcedures.find(id => item.ProcedureId === id),
      );
      const isConvertable =
        pList && pList.find((item: any) => item.IsGlobal === false);
      return isConvertable;
    } else return true;
  };

  function handleConvertToGlobalProcedure() {
    const procIds = selectedProcedures && [...selectedProcedures];
    if (procIds)
      confirmAlert({
        message: 'Are you sure you want to change the procedure(s) to global?',
        buttons: [
          {
            label: 'Yes',
            onClick: () => {
              convertToGlobalProcedure(
                {
                  procedureIds: [...procIds],
                  accountId: accountId,
                },
                {
                  onSuccess: (mutationResult: ResultModal) => {
                    if (mutationResult.IsSuccess)
                      showSuccessToast({
                        message: 'Your changes were saved successfully.',
                      });
                  },
                  onError: (error: ErrorModal) => {
                    showErrorToast({
                      message: error.Errors[0].Message,
                    });
                  },
                },
              );
            },
            className: 'pr-btn-primary',
          },
          {
            label: 'No',
            onClick: () => {
              //
            },
          },
        ],
      });
  }

  const handleConvertToNormalProcedure = () => {
    const procIds = selectedProcedures && [...selectedProcedures];

    if (procIds) {
      confirmAlert({
        message:
          'Are you sure you want to convert this global procedure to a normal procedure?',
        buttons: [
          {
            label: 'Yes',
            onClick: () => {
              convertToNormalProcedure(
                {
                  procedureIds: [...procIds],
                  accountId: accountId,
                },
                {
                  onSuccess: (mutationResult: ResultModal) => {
                    if (mutationResult.IsSuccess) {
                      showSuccessToast({
                        message: 'Your changes were saved successfully. ',
                      });
                      setSelectedProcedures([]);
                    }
                  },
                  onError: (error: ErrorModal) => {
                    showErrorToast({
                      message: error.Errors[0].Message,
                    });
                  },
                },
              );
            },
            className: 'pr-btn-primary',
          },
          {
            label: 'No',
            onClick: () => {
              //
            },
          },
        ],
      });
    }
  };

  /*********PROCEDURE CONVERSION************************************************************/

  const handleArchiveProcedures = () => {
    const procIds = selectedProcedures && [...selectedProcedures];
    if (procIds)
      confirmAlert({
        message: 'Are you sure you want to archive the procedure(s)?',
        buttons: [
          {
            label: 'Yes',
            onClick: () => {
              getProcedureReference(
                {
                  procedureIds: [...procIds],
                  isRemove: true,
                  accountId: accountId,
                },
                {
                  onSuccess: (mutationResult: any) => {
                    if (mutationResult.Data.length === 0) {
                      handleArchiveProcedureApi(procIds);
                    } else {
                      if (
                        !mutationResult.Data[0].includes(
                          '0000_POLICIESMENU.html',
                        )
                      ) {
                        confirmAlert({
                          message:
                            'This procedure has been linked to other procedures. Are you sure you want to archive the procedure?',
                          buttons: [
                            {
                              label: 'Yes',
                              onClick: () => {
                                handleArchiveProcedureApi(procIds);
                              },
                              className: 'pr-btn-primary',
                            },
                            {
                              label: 'No',
                              onClick: () => {
                                //
                              },
                            },
                          ],
                        });
                      } else {
                        handleArchiveProcedureApi(procIds);
                      }
                    }
                  },
                  onError: (error: ErrorModal) => {
                    showErrorToast({
                      message: error.Errors[0].Message,
                    });
                  },
                },
              );
            },
            className: 'pr-btn-primary',
          },
          {
            label: 'No',
            onClick: () => {
              //
            },
          },
        ],
      });
  };

  // delete procedure fn and api
  function handleArchiveProcedureApi(procIds: Array<number>) {
    archiveProcedure(
      {
        procedureIds: [...procIds],
        accountId: accountId,
      },
      {
        onSuccess: () => {
          if (procedureList)
            setSelectedProcedures([procedureList.Data[0].ProcedureId]);
          showSuccessToast({
            message: 'Your changes were saved successfully.',
          });
          setSearchValue('');
        },
        onError: (error: ErrorModal) => {
          showErrorToast({
            message: error.Errors[0].Message,
          });
        },
      },
    );
  }

  const handleDemoCall = () => {
    if (
      isUnpublishedProceduresPresent &&
      isUnpublishedProceduresPresent.length > 0
    ) {
      confirmAlert({
        message:
          'Some procedures are not yet published. Do you want to continue?',
        buttons: [
          {
            label: 'Yes',
            onClick: () => {
              window.open(
                `${process.env.REACT_APP_CALLCENTER_REDIRECT_URL}/Calls/Call/00000000-0000-0000-0000-000000000000?accountId=${accountId}&accountNumber=${accountNumber}&isFromProcedure=true`,
              );
            },
            className: 'pr-btn-primary',
          },
          {
            label: 'No',
            onClick: () => {
              //
            },
          },
        ],
      });
    } else {
      window.open(
        `${process.env.REACT_APP_CALLCENTER_REDIRECT_URL}/Calls/Call/00000000-0000-0000-0000-000000000000?accountId=${accountId}&accountNumber=${accountNumber}&isFromProcedure=true`,
      );
    }
  };

  function handlePageActionClick(funcName: () => void, conversion?: boolean) {
    let content = '';
    if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
      content = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
        procedureDetails?.Data?.AccountProcedure?.AccountId
      }/Policies/'></head><body>${(
        window as any
      )?.tinymce?.activeEditor?.getContent()}</body></html>`;
    }
    let isActive;
    if ((window as any)?.tinymce?.activeEditor?.isDirty() || userTyped) {
      if (
        (isLatestProcedurePublished ||
          (window as any)?.tinymce?.activeEditor?.isDirty() ||
          userTyped) &&
        content !== procedureDetails?.Data.Content
      ) {
        lazyRun(() => {
          //
        }, 0);
        confirmAlert({
          message:
            conversion && !isLatestProcedurePublished
              ? 'You have unpublished changes. Would you like to publish them now?'
              : 'Unsaved changes are present do you want to save the changes?',
          buttons: [
            {
              label: 'Yes',
              onClick: () =>
                saveContent(() => {
                  funcName();
                  setUserTyped(false);
                  setEditorChanges(false);
                }, conversion),
              className: 'pr-btn-primary',
            },
            {
              label: 'No',
              onClick: () => {
                if (procedureDetails?.Data) {
                  if (conversion) isActive = true;
                  else
                    isActive = procedureDetails.Data.AccountProcedure.IsActive;
                }
                setTimeout(() => {
                  funcName();
                }, 10);
              },
            },
          ],
        });
      }
    } else funcName();
  }

  const handleSavePdf = () => {
    let content = '';
    if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
      content = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
        procedureDetails?.Data?.AccountProcedure?.AccountId
      }/Policies/'></head><body>${(
        window as any
      )?.tinymce?.activeEditor?.getContent()}</body></html>`;
    }
    setContent(content);
    let payload = '';
    const pList =
      selectedProcedures &&
      procedureList?.Data.filter((item: any) =>
        selectedProcedures.find(id => item.ProcedureId === id),
      );
    pList &&
      pList.forEach((procedure: any) => {
        payload = payload + ('fileIds=' + procedure.FileId.trim() + '&');
      });
    payload = payload + 'saveMode=1';
    let pListName;
    if (pList?.length === 1) {
      pListName =
        procedureDetails?.Data.AccountProcedure.Procedure.Name.replace(
          /.html/g,
          '',
        );
      saveAsPDF({ payload, accountId, pListName, singlePdf: true });
    } else saveAsPDF({ payload, accountId, singlePdf: false });
  };

  const handleDownloadHtml = () => {
    let content = '';
    if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
      content = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
        procedureDetails?.Data?.AccountProcedure?.AccountId
      }/Policies/'></head><body>${(
        window as any
      )?.tinymce?.activeEditor?.getContent()}</body></html>`;
    }
    setContent(content);
    let payload = '';
    const pList =
      selectedProcedures &&
      procedureList?.Data.filter((item: any) =>
        selectedProcedures.find(id => item.ProcedureId === id),
      );
    pList &&
      pList.forEach((procedure: any) => {
        payload = payload + ('fileIds=' + procedure.FileId.trim() + '&');
      });

    saveAsHtml(
      { payload, accountId },
      {
        onSuccess: (response: any) => {
          const pListName =
            pList?.length === 1
              ? procedureDetails?.Data.AccountProcedure.Procedure.Name
              : 'procedure.html';
          const blob = new Blob([response], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${pListName}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
      },
    );
  };

  const handleDownloadAsWord = () => {
    let content = '';
    if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
      content = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
        procedureDetails?.Data?.AccountProcedure?.AccountId
      }/Policies/'></head><body>${(
        window as any
      )?.tinymce?.activeEditor?.getContent()}</body></html>`;
    }
    setContent(content);
    let payload = '';
    const pList =
      selectedProcedures &&
      procedureList?.Data.filter((item: any) =>
        selectedProcedures.find(id => item.ProcedureId === id),
      );
    pList &&
      pList.forEach((procedure: any) => {
        payload = payload + ('fileIds=' + procedure.FileId.trim() + '&');
      });
    payload = payload + 'saveMode=2';
    let singleProcedureName = '';
    if (pList?.length === 1) {
      pList &&
        pList.forEach((procedure: any) => {
          singleProcedureName = procedure.ProcedureName.replace('.html', '');
        });
    }
    saveAsWord(
      { payload, accountId },
      {
        onSuccess: (response: any) => {
          const source =
            'data:application/vnd.ms-word;charset=utf-8,' +
            encodeURIComponent(response);
          const fileDownload = document.createElement('a');
          document.body.appendChild(fileDownload);
          fileDownload.href = source;
          if (pList?.length === 1)
            fileDownload.download = `${singleProcedureName}.doc`;
          else fileDownload.download = 'procedure.doc';
          fileDownload.click();
          document.body.removeChild(fileDownload);
        },
      },
    );
  };

  const checkVisibilityStatus = () => {
    let pList: Array<ProcedureBuilderListType> | undefined = [];
    let value = '';
    pList =
      selectedProcedures &&
      procedureList?.Data.filter((item: any) => {
        return selectedProcedures.find(id => item.ProcedureId === id);
      });
    if (pList?.every(item => item.IsVisible === true)) {
      value = 'invisible';
    } else if (pList?.every(item => item.IsVisible === false)) {
      value = 'visible';
    } else {
      value = 'visible';
    }
    return value;
  };

  /*********MULTIPLE PROCEDURE VISIBILITY TOGGLE********************************************/
  //Do not show again checkbox//
  const handleMultipleToggleModalVisiblity = (e: any) => {
    if (e.target.checked === true) {
      localStorage.setItem(
        'multipleVisibilityModalConfirmation',
        JSON.stringify(true),
      );
    } else {
      localStorage.removeItem('multipleVisibilityModalConfirmation');
    }
  };
  //Function to handle apis
  const confirmMultipleVisibilityToggle = ({
    accountId,
    visibilityStatus,
    selectedProcedures,
  }: {
    accountId: number;
    selectedProcedures: Array<number> | null;
    visibilityStatus: number;
  }) => {
    updateProcedureVisibility(
      {
        accountId,
        visibilityStatus,
        procedureIds: selectedProcedures,
      },
      {
        onSuccess: () => {
          setSelectedProcedures([]);
        },
      },
    );
  };

  const changeMultipleProcedureVisibility = (
    visibility: boolean,
    pList: any,
  ) => {
    const tempValue = localStorage.getItem(
      'multipleVisibilityModalConfirmation',
    );
    const showConfirmation = tempValue === null ? false : JSON.parse(tempValue);
    let procedureIds: Array<number> | undefined = [];
    if (visibility === true) {
      const invisibleList =
        pList && pList.filter((item: any) => item.IsVisible !== true);
      procedureIds = invisibleList.map((item: any) => item.ProcedureId);
    } else {
      procedureIds = selectedProcedures;
    }
    const linkedProcedures =
      pList && pList.filter((item: any) => item.IsLinked);
    const isFirstTimeProcedure =
      pList &&
      pList.filter((item: any) => {
        if (item?.Procedure?.RecentVersion !== null && !item?.IsVisible)
          return item;
      });
    const globalProcedures =
      pList && pList.filter((item: any) => item?.IsGlobal);

    if (selectedProcedures)
      if (!showConfirmation) {
        confirmAlert({
          dontShowAgain: isFirstTimeProcedure.length ? false : true,
          handleModalVisibility: handleMultipleToggleModalVisiblity,
          message: isFirstTimeProcedure?.length
            ? 'One or more of the selected procedures have never been published and are not yet available to users. In order to add them to the Policies Menu, all such procedures will also be published. Proceed? '
            : visibility === true
            ? 'The selected procedure(s) will be listed under the Policies menu.'
            : 'The selected procedures will be removed from the Policies Menu, but they can still be linked to by other procedures.',
          buttons: [
            {
              label: 'Ok',
              onClick: () => {
                setTimeout(() => {
                  if (
                    linkedProcedures.length > 0 ||
                    globalProcedures.length > 0
                  ) {
                    confirmAlert({
                      message:
                        'You are about to change the visibility of a global and/or linked procedure(s). This change would affect all the other accounts to which it has been added. Do you want to proceed?',
                      buttons: [
                        {
                          label: 'Yes',
                          onClick: () => {
                            confirmMultipleVisibilityToggle({
                              accountId,
                              visibilityStatus: visibility ? 1 : 0,
                              selectedProcedures: procedureIds
                                ? procedureIds
                                : null,
                            });
                          },

                          className: 'pr-btn-primary',
                        },
                        {
                          label: 'No',
                          onClick: () => {
                            //
                          },
                        },
                      ],
                    });
                  } else {
                    confirmMultipleVisibilityToggle({
                      selectedProcedures: procedureIds ? procedureIds : null,
                      visibilityStatus: visibility ? 1 : 0,
                      accountId,
                    });
                  }
                }, 10);
              },
              className: 'pr-btn-primary',
            },
            {
              label: 'Cancel',
              onClick: () => {
                //
              },
            },
          ],
        });
      } else {
        if (linkedProcedures.length > 0 || globalProcedures.length > 0) {
          confirmAlert({
            message:
              'You are about to change the visibility of a global and/or linked procedure(s). This change would affect all the other accounts to which it has been added. Do you want to proceed?',
            buttons: [
              {
                label: 'Yes',
                onClick: () => {
                  confirmMultipleVisibilityToggle({
                    selectedProcedures: procedureIds ? procedureIds : null,
                    visibilityStatus: visibility ? 1 : 0,
                    accountId,
                  });
                },

                className: 'pr-btn-primary',
              },
              {
                label: isFirstTimeProcedure?.length ? 'Cancel' : 'No',
                onClick: () => {
                  //
                },
              },
            ],
          });
        } else {
          confirmMultipleVisibilityToggle({
            selectedProcedures: procedureIds ? procedureIds : null,
            visibilityStatus: visibility ? 1 : 0,
            accountId,
          });
        }
      }
  };

  /*********MULTIPLE PROCEDURE VISIBILITY TOGGLE*******************************************/
  const pageActions = (isProcedureCategoryUpdateInProgress: boolean) => {
    const pList =
      selectedProcedures &&
      procedureList?.Data.filter((item: any) =>
        selectedProcedures.find(id => item.ProcedureId === id),
      );
    const checkIfPoliciesMenu = pList?.some(
      (item: any) => item.ProcedureName === '0000_POLICIESMENU.html',
    );

    return [
      {
        action: `${
          checkVisibilityStatus() === 'visible'
            ? 'Mark as Visible'
            : 'Mark as Invisible'
        }`,
        icon:
          checkVisibilityStatus() === 'visible' ? (
            <Icon.Eye title=" " />
          ) : (
            <Icon.EyeOff title=" " />
          ),
        handleButtonClick: () =>
          checkVisibilityStatus() === 'visible'
            ? changeMultipleProcedureVisibility(true, pList)
            : changeMultipleProcedureVisibility(false, pList),
        disabled:
          pList?.length === 0 || checkIfPoliciesMenu
            ? true
            : false || hasGlobalProcedures,
      },
      {
        action: 'Broken Links',
        icon: <Icon.Link title=" " />,
        handleButtonClick: toggleBrokenLinks,
      },
      {
        action: `${
          pList && pList[0]?.IsGlobal === false
            ? 'Convert to global'
            : 'Convert to normal'
        }`,
        icon:
          pList && pList[0]?.IsGlobal === false ? (
            <Icon.Globe />
          ) : (
            <Icon.ConvertToLocal />
          ),
        disabled:
          pList && pList[0]?.IsGlobal === false
            ? isConvertableToGlobal()
            : isConvertableToNormal(),
        handleButtonClick: () =>
          pList && pList[0]?.IsGlobal === false
            ? handlePageActionClick(handleConvertToGlobalProcedure, true)
            : handlePageActionClick(handleConvertToNormalProcedure, true),
      },
      {
        action: 'Clone',
        icon: <Icon.Copy title=" " />,
        disabled:
          isProcedureCategoryUpdateInProgress ||
          (selectedProcedures && selectedProcedures.length >= 1 ? false : true),
        handleButtonClick: () => handlePageActionClick(handleCloneProcedure),
      },
      {
        action: 'Archive',
        icon: <Icon.Archive />,
        disabled:
          (selectedProcedures && selectedProcedures.length === 0) ||
          checkIfPoliciesMenu
            ? true
            : false,
        handleButtonClick: () => {
          handlePageActionClick(handleArchiveProcedures);
        },
      },
      {
        action: 'Save as PDF',
        icon: <Icon.PDF />,
        disabled:
          selectedProcedures && selectedProcedures.length >= 1 ? false : true,
        handleButtonClick: () => handlePageActionClick(handleSavePdf),
      },
      {
        action: 'Save as HTML',
        icon: <Icon.Code />,
        disabled:
          selectedProcedures && selectedProcedures.length >= 1 ? false : true,
        handleButtonClick: () => handlePageActionClick(handleDownloadHtml),
      },
      {
        action: 'Save as Doc',
        icon: <Icon.File />,
        disabled:
          selectedProcedures && selectedProcedures.length >= 1 ? false : true,
        handleButtonClick: () => handlePageActionClick(handleDownloadAsWord),
      },
      {
        action: 'Demo call',
        icon: <Icon.Call />,
        disabled: false,
        handleButtonClick: () => handlePageActionClick(handleDemoCall),
      },
      {
        action: 'Publish',
        icon: <Icon.ExternalLink />,
        disabled:
          selectedProcedures && selectedProcedures.length >= 1 ? false : true,
        handleButtonClick: () =>
          selectedProcedures && selectedProcedures.length === 1
            ? handlePublishSingleProcedure()
            : handlePublishMultipleProcedures(),
      },
    ];
  };

  function selectProcedure(
    isAppend: boolean,
    procedureItem: ProcedureBuilderListType,
    doubleClicked?: boolean,
  ) {
    let content = '';
    const isCurrentProcedurePolicyMenu =
      procedureDetails?.Data?.AccountProcedure?.Procedure?.Name ===
      '0000_POLICIESMENU.html';
    if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
      content = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
        procedureDetails?.Data?.AccountProcedure?.AccountId
      }/Policies/'></head><body>${(
        window as any
      )?.tinymce?.activeEditor?.getContent()}</body></html>`;
    }
    const isDirty =
      !isCurrentProcedurePolicyMenu &&
      userTyped === true &&
      selectedProcedures?.length === 1 &&
      content !== procedureDetails?.Data?.Content;

    const setSelected = () => {
      const isSelected =
        selectedProcedures &&
        selectedProcedures.find(item => item === procedureItem.ProcedureId);

      let newSelections = selectedProcedures ? [...selectedProcedures] : [];
      if (isSelected && !doubleClicked) {
        newSelections = newSelections.filter(
          item => item !== procedureItem.ProcedureId,
        );
      } else if (isAppend) {
        newSelections = [...newSelections, procedureItem.ProcedureId];
      } else newSelections = [procedureItem.ProcedureId];
      setSelectedProcedures([...newSelections]);
      // setRenameProc(undefined);(chnage made for new flow for changing selection of procedure by clicking on rename)
    };

    if (
      !isCurrentProcedurePolicyMenu &&
      ((window as any)?.tinymce?.activeEditor?.isDirty() || isDirty)
    ) {
      if (procedureDetails?.Data?.AccountProcedure.Procedure.IsAutoSave) {
        lazyRun(() => {
          //
        }, 0);
        saveContent(() => {
          setSelected();
          setUserTyped(false);
          setEditorChanges(false);
        });
      } else {
        lazyRun(() => {
          //
        }, 0);
        confirmAlert({
          message:
            'Unsaved changes are present do you want to save the changes?',
          buttons: [
            {
              label: 'Yes',
              onClick: () =>
                saveContent(() => {
                  setSelected();
                  setUserTyped(false);
                  setEditorChanges(false);
                }),
              className: 'pr-btn-primary',
            },
            {
              label: 'No',
              onClick: () => {
                setSelected();
              },
            },
          ],
        });
      }
    } else {
      setSelected();
    }
  }

  const isLatestProcedurePublished = useMemo(() => {
    const pList = procedureList?.Data.filter(
      (item: any) =>
        selectedProcedures &&
        selectedProcedures.find(id => item.ProcedureId === id),
    );
    if (pList && pList[0]?.HasSavedChangesThatAreNotPublished === true) {
      return false;
    } else return true;
  }, [procedureList, selectedProcedures]);

  return {
    brokenLinks: brokenLinks?.Data,
    accountInfo,
    procedures,
    procedureDetails,
    selectedProcedures,
    searchValue,
    renameProc,
    totalBrokenLinksCount,
    procedureList,
    updateProcedureVisibility,
    setSelectedProcedures,
    getProcedureReference,
    updateProcedureAutoSaveOption,
    renameProcedure,
    setProcedures,
    setSearchValue,
    setRenameProc,
    saveContent,
    pageActions,
    createProcedure,
    selectProcedure,
    undoProcedureContent,
    appliedFilterOption,
    setAppliedFilterOption,
    isLoading,
    setUserTyped,
    editorChanges,
    setEditorChanges,
    userTyped,
    content,
    setContent,
    saveTemplate,
    updateTemplate,
    createBlobDirectory,
    uploadFile,
    deleteFile,
    showBrokenLinks,
    toggleBrokenLinks,
    autoSaveContent,
    autoPublishPolicyContent,
    isSaveProcedureLoading,
    formatCleanProcedure,
    forceRenameProcedure,
    refetchProcedureDetails: refetch,
  };
}
